import { MouseEvent, PointerEvent, useCallback, useMemo, useRef, useState } from 'react';
import { TIMELINE_END_MINUTES, TIMELINE_START_MINUTES } from '../../constants';
import { areSlotsEqual } from '../../helpers/helpers';
import {
  clampToRange,
  clientYToTimelineMinutes,
  createFocusSlot,
  getMinMovableStartMinutes,
  getSlotEndMinutes,
  getSlotStartMinutes,
  overlapsAny,
  roundToStep,
} from '../../helpers/time';
import { useFocusTimeSlotActions } from '../../hooks/useFocusTimeSlotActions';
import { FocusTimeHandle, FocusTimeSlot } from '../../types';
import { FocusTimeDayTimelineSlot, MovingSlotState } from './types';

type UseFocusTimeDayTimelineActionsParams = Readonly<{
  currentMinutes: number | null;
  onChange: (slots: FocusTimeDayTimelineSlot[]) => void;
  slots: FocusTimeDayTimelineSlot[];
}>;

const EMPTY_SLOTS: FocusTimeDayTimelineSlot[] = [];

const useFocusTimeDayTimelineActions = ({
  currentMinutes,
  onChange,
  slots: propsSlots,
}: UseFocusTimeDayTimelineActionsParams) => {
  const dayRef = useRef<HTMLDivElement | null>(null);
  const [draggingSlotId, setDraggingSlotId] = useState<string | null>(null);
  const [movingSlot, setMovingSlot] = useState<MovingSlotState | null>(null);
  const slots = propsSlots || EMPTY_SLOTS;
  const minMovableStartMinutes = getMinMovableStartMinutes(currentMinutes);

  const commitSlots = useCallback(
    (producer: (previousSlots: FocusTimeSlot[]) => FocusTimeSlot[]) => {
      const nextSlots = [...producer(slots)].sort(
        (left, right) => getSlotStartMinutes(left) - getSlotStartMinutes(right),
      );

      if (!areSlotsEqual(slots, nextSlots)) {
        onChange(nextSlots);
      }
    },
    [onChange, slots],
  );

  const { addSlotAt, removeSlot, updateSlot } = useFocusTimeSlotActions({ commitSlots });

  const moveSlot = useCallback(
    (slotId: string, startMinutes: number, durationMinutes: number) => {
      commitSlots(previousSlots => {
        const previousSlot = previousSlots.find(slot => slot.id === slotId);

        if (!previousSlot) {
          return previousSlots;
        }

        const maxStartMinutes = TIMELINE_END_MINUTES - durationMinutes;
        const minStartMinutes = Math.min(minMovableStartMinutes, maxStartMinutes);
        const nextStartMinutes = clampToRange(roundToStep(startMinutes), minStartMinutes, maxStartMinutes);
        const nextSlot = createFocusSlot(
          previousSlot.id,
          nextStartMinutes,
          nextStartMinutes + durationMinutes,
          previousSlot.startAt,
        );

        if (overlapsAny(nextSlot, previousSlots)) {
          return previousSlots;
        }

        return previousSlots.map(slot => (slot.id === slotId ? nextSlot : slot));
      });
    },
    [commitSlots, minMovableStartMinutes],
  );

  const timelineSlots = useMemo(
    () =>
      slots.filter(slot => {
        const startMinutes = getSlotStartMinutes(slot);
        const endMinutes = getSlotEndMinutes(slot);

        return (
          startMinutes >= TIMELINE_START_MINUTES && endMinutes <= TIMELINE_END_MINUTES && startMinutes < endMinutes
        );
      }),
    [slots],
  );

  const addSlotFromTimeline = useCallback(
    (event: MouseEvent<HTMLDivElement>) => {
      const target = event.target as HTMLElement;

      if (target.closest('[data-focus-time-slot]') || !target.closest('[data-focus-time-hour]')) {
        return;
      }

      addSlotAt(clientYToTimelineMinutes(event.clientY, event.currentTarget));
    },
    [addSlotAt],
  );

  const startResize = useCallback(
    (slotId: string, handle: FocusTimeHandle) => (event: PointerEvent<HTMLButtonElement>) => {
      if (event.currentTarget.disabled || !dayRef.current) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      event.currentTarget.setPointerCapture(event.pointerId);
      setDraggingSlotId(slotId);
      updateSlot(slotId, handle, clientYToTimelineMinutes(event.clientY, dayRef.current));
    },
    [updateSlot],
  );

  const moveResize = useCallback(
    (slotId: string, handle: FocusTimeHandle) => (event: PointerEvent<HTMLButtonElement>) => {
      if (!event.currentTarget.hasPointerCapture(event.pointerId) || !dayRef.current) {
        return;
      }

      updateSlot(slotId, handle, clientYToTimelineMinutes(event.clientY, dayRef.current));
    },
    [updateSlot],
  );

  const endResize = useCallback((event: PointerEvent<HTMLButtonElement>) => {
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }

    setDraggingSlotId(null);
  }, []);

  const startSlotMove = useCallback(
    (slot: FocusTimeDayTimelineSlot) => (event: PointerEvent<HTMLDivElement>) => {
      const target = event.target as HTMLElement;

      if (target.closest('button') || !dayRef.current) {
        return;
      }

      const startMinutes = getSlotStartMinutes(slot);
      if (startMinutes < minMovableStartMinutes) {
        return;
      }

      event.preventDefault();
      event.currentTarget.setPointerCapture(event.pointerId);
      setDraggingSlotId(slot.id);
      setMovingSlot({
        id: slot.id,
        pointerStartMinutes: clientYToTimelineMinutes(event.clientY, dayRef.current),
        slotStartMinutes: startMinutes,
        durationMinutes: getSlotEndMinutes(slot) - startMinutes,
      });
    },
    [minMovableStartMinutes],
  );

  const moveSlotByPointer = useCallback(
    (event: PointerEvent<HTMLDivElement>) => {
      if (!movingSlot || !event.currentTarget.hasPointerCapture(event.pointerId) || !dayRef.current) {
        return;
      }

      const pointerMinutes = clientYToTimelineMinutes(event.clientY, dayRef.current);
      moveSlot(
        movingSlot.id,
        movingSlot.slotStartMinutes + pointerMinutes - movingSlot.pointerStartMinutes,
        movingSlot.durationMinutes,
      );
    },
    [moveSlot, movingSlot],
  );

  const endSlotMove = useCallback((event: PointerEvent<HTMLDivElement>) => {
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }

    setDraggingSlotId(null);
    setMovingSlot(null);
  }, []);

  return {
    dayRef,
    draggingSlotId,
    timelineSlots,
    minMovableStartMinutes,
    addSlotFromTimeline,
    startResize,
    moveResize,
    endResize,
    startSlotMove,
    moveSlotByPointer,
    endSlotMove,
    removeSlot,
  };
};

export default useFocusTimeDayTimelineActions;
